import { useParams } from 'react-router-dom';
import { useTenantGet } from '../hooks/use-tenant.hook';
import { memo, useMemo } from 'react';
import SysAdminTenantDetailsComponent from '../components/sysadmin-tenant-details.component';
import { DetailItem } from '../../../../common/RenderDetails';
import { BrandingColors, Tenant } from '../types/tenant.types';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Country } from '../../countries/types/country.types';
import { WithSysAdminPermissionCheck } from '../../../../auth/WithSysAdminPermissionCheck';
import PageTitle from '@components/common/PageTitle';

const SysAdminTenantDetailsContainer = () => {
  const { id } = useParams();
  const { t } = useTranslation();

  const { data, isLoading, error } = useTenantGet(id ?? '');

  const prepareDetailsData = useMemo(
    () =>
      (dataObj: Tenant | undefined): DetailItem[] => {
        if (!dataObj) return [];

        const detailItems: DetailItem[] = [];
        // prepare data to display in the details card
        Object.entries(dataObj).forEach(([key, value]) => {
          if (key === 'country') {
            detailItems.push({
              label: t('tenants.countryId.label'),
              value: (value as Country)?.code ?? '-',
            });
            return;
          }

          if (key === 'contractStartDate' || key === 'contractEndDate') {
            detailItems.push({
              label: t(`tenants.${key}.label`),
              value: value ? dayjs(value as Date).format('YYYY-MM-DD') : '-',
            });
            return;
          }

          if (key === 'createdAt' || key === 'updatedAt') {
            detailItems.push({
              label: t(`tenants.${key}.label`),
              value: value ? new Date(value as Date).toLocaleString() : '-',
            });
            return;
          }

          if (key === 'brandingColors') {
            const brandingColors = value as BrandingColors;
            detailItems.push({
              label: t(`tenants.${key}.label`),
              value: value
                ? `primary: ${brandingColors.primary}, secondary: ${brandingColors.secondary}`
                : '-',
            });
            return;
          }

          detailItems.push({
            label: t(`tenants.${key}.label`),
            value: String(value),
          });
        });

        return detailItems;
      },
    [data?.data, t]
  );

  if (!id)
    return (
      <ErrorFallback
        details={t('crud.details.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('common.invalidIdProvided'))}
        resetErrorBoundary={() => {}}
      />
    );

  if (error) {
    return (
      <>
        <PageTitle title={t('crud.details.title', { entity: t('tenants.title') })} />
        <HeaderRow
          title={t('crud.details.title', { entity: t('tenants.title') })}
          showBackButton={true}
        />
        <ErrorFallback
          details={t('crud.details.errorLoadingData')}
          icon={<ExclamationCircleOutlined />}
          error={error}
          resetErrorBoundary={() => {}}
        />
      </>
    );
  }

  return (
    <>
      <PageTitle title={t('crud.details.title', { entity: t('tenants.title') })} />
      <HeaderRow
        title={t('crud.details.title', { entity: t('tenants.title') })}
        showBackButton={true}
        isSysAdmin={true}
        trailTarget={id}
      />
      <div className="row mt-3">
        <SysAdminTenantDetailsComponent
          data={prepareDetailsData(data?.data)}
          success={data?.success ?? false}
          isLoading={isLoading}
          error={error}
        />
      </div>
    </>
  );
};

export default WithSysAdminPermissionCheck(SysAdminTenantDetailsContainer, 'tenant.view');
